// Phase 14 (2026-06-28) — Centralized admin transaction actions.
//
// Bug bundle #14 (2026-08-19) — the legacy pencil-edit button was retired.
// The New JE editability workflow (Edit metadata for safe fields + Reverse &
// Correct for material changes) is now the single supported path and both
// buttons are surfaced at the top of the JE detail modal. This component
// now only renders the Delete (trash) action.
//
// One shared component used wherever a transaction appears (transactions
// list, bookkeeping JE drill-down, AP/AR rows, bank tabs, payroll, loans,
// invoices, reports drill-down…). Renders the Delete (trash) icon only for
// `system_admin` / full_access users; everyone else sees nothing.
//
// Delete flow: confirms with a required "Reason" textarea, then DELETE
// /api/transactions/{id}?reason=… → backend posts a reversing JE when the
// tx was already posted.

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Phase 19 hot-fix #3 (2026-07-07) — Posted statuses match the backend
// (see server.update_transaction `POSTED_STATUSES`). Anything in this set
// requires the `_posted` companion key.
const POSTED_STATUSES = new Set(["processed", "posted", "reported"]);

export function AdminTxActions({ tx, onChanged, size = "sm" }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!tx || !tx.id) return null;

  const isPosted = POSTED_STATUSES.has(tx.status);
  const canDelete = can?.("transactions.delete") &&
    (!isPosted || can?.("transactions.delete_posted"));

  if (!canDelete) return null;

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const onAskDelete = (e) => { stop(e); setReason(""); setOpen(true); };

  const onConfirmDelete = async () => {
    const r = reason.trim();
    if (!r) {
      toast.error("Reason is required for an admin delete (audit-trail).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/transactions/${tx.id}`, { data: { reason: r } });
      toast.success(data.soft
        ? "Transaction soft-deleted (reversing JE posted)."
        : "Transaction permanently removed.");
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size === "icon" ? "icon" : "sm"}
        variant="outline"
        onClick={onAskDelete}
        title="Delete transaction"
        data-testid={`admin-delete-tx-${tx.id}`}
      >
        <Trash2 className="h-4 w-4 text-rose-600" />
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="admin-delete-tx-dialog" onClick={stop}>
          <DialogHeader>
            <DialogTitle className="text-rose-700">Delete transaction (admin override)</DialogTitle>
            <DialogDescription>
              You are about to delete{" "}
              <span className="font-mono text-slate-700">
                {tx.reference_number || tx.id?.slice(0, 8)}
              </span>{" "}
              ({tx.status}). If the transaction has been posted to the GL, a
              <strong> reversing journal entry </strong>
              will be created. The original record is preserved as
              <code> deleted_by_admin </code> for audit. A reason is required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason (required)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this transaction being voided?"
              rows={3}
              data-testid="admin-delete-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onConfirmDelete} disabled={busy || !reason.trim()}
                    data-testid="confirm-admin-delete">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trash2 className="mr-2 h-4 w-4" /> Confirm delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdminTxActions;
